/**
 * Unified client-side auth storage wipe (localStorage, sessionStorage, OIDC, cookies, IndexedDB).
 */
import { clearAuthCookies } from '@/lib/utils'
import { getApiContextPath } from '@/lib/utils/url-normalization'

export type WipeAppClientStorageOptions = {
  preserveTheme?: boolean
  sessionMessage?: string | null
  /** When true, sets logout_completed and logout_timestamp after wipe. */
  setLogoutFlags?: boolean
}

const KNOWN_AUTH_COOKIE_NAMES = ['XSRF-TOKEN', 'aegis_session_id', 'sidebar:state']

export function clearOidcLocalStorageKeys(): void {
  if (typeof window === 'undefined') return
  const oidcKeys: string[] = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key && (key.startsWith('oidc.') || key.startsWith('oidc-client'))) {
      oidcKeys.push(key)
    }
  }
  oidcKeys.forEach((key) => localStorage.removeItem(key))
}

/**
 * Clear only restored OIDC *user* profiles (`oidc.user:…`).
 * Must NOT remove PKCE / state keys (`oidc.{state}`) — those are required
 * for `signinRedirectCallback` after returning from the IdP.
 */
export function clearOidcUserLocalStorageKeys(): void {
  if (typeof window === 'undefined') return
  const keys: string[] = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key?.startsWith('oidc.user:')) {
      keys.push(key)
    }
  }
  keys.forEach((key) => localStorage.removeItem(key))
}

/** Expire known auth cookies at common API paths (HttpOnly cookies require server Set-Cookie). */
export function clearAppAuthCookies(): void {
  if (typeof document === 'undefined') return
  clearAuthCookies()
  const contextPath = getApiContextPath()
  const paths = ['/', contextPath].filter((p, i, arr) => p && arr.indexOf(p) === i)
  const host = window.location.hostname
  const domainParts = host.split('.').filter(Boolean)
  const domainCandidates = new Set<string>([''])
  for (let i = 0; i < domainParts.length - 1; i++) {
    domainCandidates.add(`.${domainParts.slice(i).join('.')}`)
  }
  KNOWN_AUTH_COOKIE_NAMES.forEach((name) => {
    paths.forEach((path) => {
      domainCandidates.forEach((domain) => {
        const domainAttr = domain ? `;domain=${domain}` : ''
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path}${domainAttr};SameSite=Lax`
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path}${domainAttr};Secure;SameSite=None`
      })
    })
  })
}

export async function deleteOidcIndexedDb(): Promise<void> {
  if (typeof window === 'undefined' || !('indexedDB' in window)) return
  try {
    await indexedDB.deleteDatabase('oidc-client')
  } catch {
    /* ignore */
  }
}

/**
 * Wipe browser auth state while optionally preserving theme and post-logout flags.
 */
export function wipeAppClientStorage(options?: WipeAppClientStorageOptions): void {
  if (typeof window === 'undefined') return

  const preserveTheme = options?.preserveTheme !== false
  const sessionMessage = options?.sessionMessage ?? null
  const setLogoutFlags = options?.setLogoutFlags !== false

  clearOidcLocalStorageKeys()
  clearAppAuthCookies()

  const theme = preserveTheme ? localStorage.getItem('theme') : null
  const logoutCompleted = sessionStorage.getItem('logout_completed')
  const logoutTimestamp = sessionStorage.getItem('logout_timestamp')
  const existingSessionMessage = sessionStorage.getItem('sessionMessage')

  localStorage.clear()
  if (theme) {
    localStorage.setItem('theme', theme)
  }

  sessionStorage.clear()

  const messageToKeep = sessionMessage ?? existingSessionMessage
  if (messageToKeep) {
    sessionStorage.setItem('sessionMessage', messageToKeep)
  }
  if (setLogoutFlags) {
    sessionStorage.setItem('logout_completed', logoutCompleted ?? 'true')
    sessionStorage.setItem('logout_timestamp', logoutTimestamp ?? Date.now().toString())
  }
}

export async function wipeAppAuthState(options?: WipeAppClientStorageOptions): Promise<void> {
  wipeAppClientStorage(options)
  await deleteOidcIndexedDb()
}
